import { useCallback, useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, IconButton, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useNavigate } from 'react-router';
import { configureColumnComparisonChart } from '../../ChartConfiguration/ChartConfiguration';
import { fetchSubmittedClaimsChart, MenuProps } from '../../Widgets/WidgetService';
import EncounterSummary from '../../../Models/Widget/EncounterSummary.model';
import Authorization from '../../../utils/Authorization';
import { useWidgetContext } from '../../../hooks/widgetContext';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import '../Widgets.css';

const SubmittedClaimSummary = (props) => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [encounterSummary, setEncounterSummary] = useState<EncounterSummary>();
    const [encounterOptions, setEncounterOptions] = useState({});
    const [isEncounterGraphShow, setIsEncounterGraphShow] = useState<boolean>(false);
    const {
        submittedClaimSelectedProvider,
        setSubmittedClaimSelectedProvider,
        submittedClaimSelectedPlans,
        setSubmittedClaimSelectedPlans,
        submittedClaimSelectedTeam,
        setSubmittedClaimSelectedTeam,
        submittedClaimShowInActiveTeams,
        setSubmittedClaimShowInActiveTeams,
        submittedClaimShowInActiveProviders,
        setSubmittedClaimShowInActiveProviders,
    } = useWidgetContext();
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const filteredProviders = (submittedClaimShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const chartHeight = 450;

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isEncounterGraphShow) {
            setEncounterOptions(
                configureColumnComparisonChart(
                    'Submitted Claim Summary',
                    'Claims Count',
                    'Day',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    null,
                    chartHeight
                )
            );
        }

        setSubmittedClaimSelectedTeam(authData.getDefaultTeamId(''));
        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, setIsFetched, isEncounterGraphShow]);
    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const changeFilter = async () => {
            const data = await fetchSubmittedClaimsChart(
                submittedClaimSelectedProvider,
                submittedClaimSelectedTeam,
                submittedClaimSelectedPlans.join(","),
                submittedClaimShowInActiveTeams,
                submittedClaimShowInActiveProviders,
                false
            );
            setEncounterSummary(data);
        };
        setIsEncounterGraphShow(false);
        setEncounterOptions(
            configureColumnComparisonChart(
                'Submitted Claim Summary',
                'Claims Count',
                'Day',
                'Count',
                [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                [0],
                null,
                chartHeight
            )
        );
        changeFilter();
    }, [
        submittedClaimSelectedProvider,
        submittedClaimSelectedPlans,
        submittedClaimSelectedTeam,
        submittedClaimShowInActiveTeams,
        submittedClaimShowInActiveProviders,
    ]);

    useEffect(() => {
        if (encounterSummary) {
            let xAxisValues = [];
            let yAxisValues = [];
            let totClaimsValues = [];
            let submittedClaimsValues = [];
            for (var data in encounterSummary.Encounters) {
                let dataInfo = encounterSummary.Encounters[data];

                let percentage = (dataInfo.CleanClaimCount / dataInfo.EncounterCount) * 100;

                let d = {
                    y: dataInfo.CleanClaimCount,
                    name: Math.round(percentage),
                };
                submittedClaimsValues.push(d);

                let d1 = {
                    y: dataInfo.EncounterCount,
                    name: '',
                };
                totClaimsValues.push(d1);

                xAxisValues.push(dataInfo.Date);
            }
            yAxisValues.push({ name: 'Total Claims', data: totClaimsValues, color: '#4682B4', showInLegend: true });
            yAxisValues.push({ name: 'Submitted Claims', data: submittedClaimsValues, color: '#b0dbde', showInLegend: true });

            if (encounterSummary.Encounters.length === 0) {
                setEncounterOptions(
                    configureColumnComparisonChart(
                        'Submitted Claim Summary',
                        'Claims Count',
                        'Day',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        null,
                        chartHeight
                    )
                );
            } else {
                let subTitle = '',
                    xAxisTitle = '',
                    yAxisTitle = 'Count';
                setEncounterOptions(configureColumnComparisonChart('Submitted Claim Summary', subTitle, xAxisTitle, yAxisTitle, yAxisValues, xAxisValues, null, chartHeight));
            }
            setIsEncounterGraphShow(true);
        }
    }, [encounterSummary]);

    const toggleProviderHandler = (isChecked: boolean) => {
        setSubmittedClaimShowInActiveProviders(isChecked);
        setSubmittedClaimSelectedProvider('All');
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/submittedclaimsummarydetail?hasElevatedFilterAccess=${props.hasElevatedFilterAccess}`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                            <span className="pointer" onClick={navigateToMembers}>
                                Submitted Claims Within 72 Hours
                            </span>
                            <Tooltip
                                open={show}
                                title={
                                    <div>
                                        <Typography variant="caption">Claims submitted in last 72 hours</Typography>
                                    </div>
                                }
                                PopperProps={{ style: { zIndex: 1000 } }}
                            >
                                <IconButton onClick={handleClick}>
                                    <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>
                    {props.hasElevatedFilterAccess && (
                        <Grid size={4}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={submittedClaimSelectedTeam}
                                onChange={(id) => setSubmittedClaimSelectedTeam(id)}
                                showInactive={submittedClaimShowInActiveTeams}
                                onToggleInactive={setSubmittedClaimShowInActiveTeams}
                            />
                        </Grid>)}
                    <Grid size={4}>
                        <HealthPlanMultiSelect
                            value={submittedClaimSelectedPlans}
                            onChange={setSubmittedClaimSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Providers</InputLabel>
                            <Select
                                value={submittedClaimSelectedProvider}
                                label="Providers"
                                name="Providers"
                                onChange={(e) => setSubmittedClaimSelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={
                                        <Checkbox checked={submittedClaimShowInActiveProviders} onChange={(e) => toggleProviderHandler(e.target.checked)} />
                                    }
                                    label="Show Inactive"
                                />
                                {filteredProviders.map((option) => {
                                    return (
                                        <MenuItem key={option.Id} value={option.Id} sx={{ color: option.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {`${option.LastName}, ${option.FirstName}`}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={12} marginTop={1} sx={{ position: 'relative', paddingTop: '50px !important' }}>
                        {!isEncounterGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={encounterOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default SubmittedClaimSummary;
